// =====================================================
// COMPOSANT: StatisticsDashboard — dashboard stats natif (sans wrapper de page)
// =====================================================
// RETOUCHE 2 : le dashboard vit désormais dans l'onglet « Statistiques » du
// Tableau de bord (HomePage), plus dans une page dédiée. On extrait ici le corps
// de l'ancienne DashboardStatsPage SANS PageHeader/PageLayout (HomePage fournit
// déjà le breadcrumb, le titre et les onglets). Filtre date-picker + state
// DashboardFilters + toutes les sections (KPI → Flux → Profil → Appels →
// Conversion → Origine → Tableau de Romain) vivent dans ce composant.

import { useMemo, useState } from 'react';
import {
  BadgeEuro,
  UserPlus,
  UserCheck,
  UserRoundSearch,
  ShieldAlert,
  ShieldCheck,
  Hourglass,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { FilterBar } from '@/components/dashboard-stats/FilterBar';
import { KpiCard } from '@/components/dashboard-stats/KpiCard';
import { FluxSection } from '@/components/dashboard-stats/FluxSection';
import { ConversionSection } from '@/components/dashboard-stats/ConversionSection';
import { ProfileSection } from '@/components/dashboard-stats/ProfileSection';
import { AppelsSection } from '@/components/dashboard-stats/AppelsSection';
import { OriginSection } from '@/components/dashboard-stats/OriginSection';
import { RomainSection } from '@/components/dashboard-stats/RomainSection';
import { ExportCsvButton } from '@/components/dashboard-stats/ExportCsvButton';
import { KpiDetailModal } from '@/components/dashboard-stats/KpiDetailModal';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import {
  defaultDateWindow,
  shiftWindowYears,
  toIsoDate,
  windowFromDates,
  KPI_DESCRIPTIONS,
  KPI_LABELS,
  type KpiKey,
} from '@/lib/dashboard-stats';

const KPI_ICONS: Record<KpiKey, LucideIcon> = {
  bpi: BadgeEuro,
  pi: UserPlus,
  id: UserCheck,
  pr: UserRoundSearch,
  derogations: ShieldAlert,
  derogations_acceptees: ShieldCheck,
  pi_attente: Hourglass,
};

// Ordre d'affichage des cartes (BPI/PI/ID/PR + dérogations attente/acceptée + BPI en attente).
const KPI_ORDER: KpiKey[] = [
  'bpi',
  'pi',
  'id',
  'pr',
  'derogations',
  'derogations_acceptees',
  'pi_attente',
];

/** 'yyyy-mm-dd' → 'dd/mm/yyyy' (libellé FR de plage). */
function frDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Corps du dashboard stats, rendu à l'intérieur de l'onglet « Statistiques » du
 * Tableau de bord. Se glisse dans le conteneur scrollable de HomePage (pas de
 * gestion de scroll ni de fond ici — c'est HomePage qui les porte).
 */
export function StatisticsDashboard() {
  // Carte dont le détail est ouvert (issue #228). null = modale fermée.
  const [detailKpi, setDetailKpi] = useState<KpiKey | null>(null);

  // Instant de référence figé au montage (évite un `now` mouvant entre renders).
  const [now] = useState(() => new Date());
  const initialRange = useMemo(() => defaultDateWindow(now), [now]);
  const [startDate, setStartDate] = useState(initialRange.startIso);
  const [endDate, setEndDate] = useState(initialRange.endIso);
  const [showN1, setShowN1] = useState(true);
  const [editionId, setEditionId] = useState<string | null>(null);

  // Fenêtre N (mémoïsée sur les bornes) + libellés de plage.
  const window = useMemo(() => windowFromDates(startDate, endDate), [startDate, endDate]);
  const rangeLabel = `${frDate(startDate)} → ${frDate(endDate)}`;
  const n1RangeLabel = useMemo(() => {
    const prev = shiftWindowYears(window, -1);
    return `${frDate(toIsoDate(prev.start))} → ${frDate(toIsoDate(prev.end))}`;
  }, [window]);

  const {
    loading,
    editions,
    kpis,
    currentEdition,
    flux,
    histogram,
    conversion,
    profile,
    appels,
    origin,
    romain,
    rawCounts,
  } = useDashboardStats(editionId, window, now);

  const hasN1 = !!currentEdition?.prevEditionId;

  const editionYearLabel = useMemo(() => {
    if (!currentEdition) return '';
    return currentEdition.year ? `Édition ${currentEdition.year}` : currentEdition.name;
  }, [currentEdition]);

  // Issue #228 : les 6 cartes ouvrent le même drill-down. Avant, seules PR/PI/ID
  // étaient cliquables et menaient à une liste NON filtrée (écart garanti avec le
  // chiffre de la carte) ; BPI, Dérogations et PI en attente n'avaient aucune
  // destination. La modale requête les mêmes prédicats que le compte affiché.
  const detailContext = useMemo(
    () => ({ editionId: currentEdition?.id ?? null, window }),
    [currentEdition?.id, window]
  );

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Titre de section + export CSV (pas de « Tableau de bord » : HomePage le porte déjà) */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white px-6 py-5">
        <h2 className="text-lg font-semibold text-zinc-900">
          Statistiques{editionYearLabel ? ` — ${editionYearLabel}` : ''}
          <span className="ml-2 text-sm font-normal text-zinc-500">· tunnel BPI → PI → ID</span>
        </h2>
        {kpis ? (
          <ExportCsvButton
            kpis={kpis}
            conversion={conversion}
            appels={appels}
            origin={origin}
            romain={romain}
            showN1={showN1}
            rangeLabel={rangeLabel}
            editionName={editionYearLabel || currentEdition?.name || 'edition'}
            disabled={loading}
          />
        ) : null}
      </div>

      {/* Barre de filtres */}
      <div className="rounded-lg border border-zinc-200 bg-white px-6 py-4">
        <FilterBar
          editions={editions}
          editionId={currentEdition?.id ?? null}
          onEditionChange={setEditionId}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          showN1={showN1}
          onShowN1Change={setShowN1}
          n1RangeLabel={n1RangeLabel}
        />
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-2 text-zinc-500">
            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-zinc-900" />
            <span>Chargement…</span>
          </div>
        </div>
      ) : !kpis ? (
        <div className="flex items-center justify-center py-20 text-sm text-zinc-400">
          Aucune édition disponible dans la base.
        </div>
      ) : (
        <>
          {/* ── KPI globaux ─────────────────────────────────────────────── */}
          <div className="flex items-baseline gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
              Indicateurs globaux
            </h3>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
            {KPI_ORDER.map((key) => (
              <div key={key}>
                <KpiCard
                  label={KPI_LABELS[key]}
                  description={KPI_DESCRIPTIONS[key]}
                  icon={KPI_ICONS[key]}
                  kpi={kpis[key]}
                  showN1={showN1}
                  onClick={() => setDetailKpi(key)}
                />
              </div>
            ))}
          </div>

          {/* Bandeau de contexte / preuve des chiffres réels */}
          <p className="text-xs text-zinc-400">
            {rawCounts.crews} équipages · {rawCounts.bpi} BPI payés · {rawCounts.prospects}{' '}
            prospects · {rawCounts.preinscriptions} pré-inscriptions chargés depuis la base. Plage{' '}
            {rangeLabel} · comparaison N-1{' '}
            {currentEdition?.prevEditionId
              ? `active (${n1RangeLabel})`
              : 'indisponible (édition N-1 absente)'}
            .
          </p>

          {/* ── Flux d'entrées ──────────────────────────────────────────── */}
          {flux && histogram ? (
            <FluxSection flux={flux} histogram={histogram} showN1={showN1} hasN1={hasN1} />
          ) : null}

          {/* ── Profil des équipages + Corps de métier & études ─────────── */}
          {profile ? (
            <ProfileSection
              sex={profile.sex}
              age={profile.age}
              metierEtudes={profile.metierEtudes}
            />
          ) : null}

          {/* ── Suivi des appels par équipage ───────────────────────────── */}
          {appels ? <AppelsSection appels={appels} /> : null}

          {/* ── Conversion & traçabilité commerciale ────────────────────── */}
          {conversion ? <ConversionSection conversion={conversion} showN1={showN1} /> : null}

          {/* ── Origine des PI ──────────────────────────────────────────── */}
          {origin ? <OriginSection origin={origin} showN1={showN1} /> : null}

          {/* ── Tableau de Romain — TOUTE FIN de page ───────────────────── */}
          {romain ? <RomainSection romain={romain} showN1={showN1} /> : null}

          {/* Détail d'une carte KPI (issue #228) */}
          <KpiDetailModal
            kpiKey={detailKpi}
            onClose={() => setDetailKpi(null)}
            context={detailContext}
            cardValue={detailKpi ? kpis[detailKpi].value : 0}
            rangeLabel={rangeLabel}
            editionLabel={editionYearLabel || currentEdition?.name || 'Édition'}
          />
        </>
      )}
    </div>
  );
}

export default StatisticsDashboard;
